// 2026-08-15 éles hiba: az app több helyen a `createdAt` Firestore-
// timestampet (mikor íródott BE a bejegyzés) használta a `date` mező
// (mikor TÖRTÉNT, amit a user megadott) helyett — grafikonokhoz,
// rendezéshez, időszak-szűréshez, "hányadik hete" számításhoz.
// Visszamenőleges rögzítésnél (pl. egy év adatának egyszerre felvitele)
// ez a hiba: minden bejegyzés ugyanazt a (mai) dátumot mutatta, rossz
// sorrendben jelentek meg, és az időszak-szűrők/grafikonok összesűrítették
// a valós idősort a felvitel pillanatára.
//
// Elv innentől: a felhasználónak megjelenített és a számításokban
// használt időpont MINDIG a `date` mező. A `createdAt` kizárólag
// technikai metaadat — csak akkor esünk vissza rá, ha a `date` hiányzik
// (régi, a mező bevezetése előtti adat).

export interface DatedEntry {
  date?: string;
  createdAt?: { seconds: number };
}

/** Az entry "valós" időpontja ezredmásodpercben — date elsőbbséggel. */
export function entryTimestampMs(e: DatedEntry): number {
  if (e.date) {
    const t = new Date(e.date).getTime();
    if (!isNaN(t)) return t;
  }
  return e.createdAt?.seconds ? e.createdAt.seconds * 1000 : 0;
}

/** Bejegyzések date szerint, növekvő sorrendben (legrégebbi elöl). */
export function sortEntriesByDateAsc<T extends DatedEntry>(entries: T[]): T[] {
  return [...entries].sort((a, b) => entryTimestampMs(a) - entryTimestampMs(b));
}

/** Bejegyzések date szerint, csökkenő sorrendben (legfrissebb elöl). */
export function sortEntriesByDateDesc<T extends DatedEntry>(entries: T[]): T[] {
  return [...entries].sort((a, b) => entryTimestampMs(b) - entryTimestampMs(a));
}

/** Rövid, magyar dátumcímke (pl. "aug. 8.") grafikon-tengelyekhez. */
export function formatEntryDateLabel(e: DatedEntry): string {
  const ms = entryTimestampMs(e);
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("hu-HU", { month: "short", day: "numeric" });
}

/** "YYYY-MM-DD" a megadott Date HELYI naptári napjából — nem toISOString
 * (ami UTC-t adna, és éjfél körül más napra eshetne, mint amit a user
 * ténylegesen a natív dátumválasztóban lát). */
export function localDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Jövőbeli-e a bejegyzés — NAPTÁRI NAP szerint (a böngésző helyi ideje),
 * nem nyers timestamp-összehasonlítással. A `date` egy puszta "YYYY-MM-DD"
 * string (a natív dátumválasztó helyi naptári napja), ezért string-
 * összehasonlítás elég, és elkerüli azt a hibaosztályt, ahol egy bare
 * dátum-string UTC éjfélként értelmeződik: éjfél körül (pl. 23:50) a MAI
 * bejegyzés így soha nem esik tévesen "jövőbelibe".
 *
 * `createdAt`-ra visszaeső bejegyzésnél (nincs `date`) nincs mit jövőbelinek
 * tekinteni — a technikai beviteli időbélyeg sosem jövőbeli.
 */
export function isFutureEntry(e: DatedEntry, now: Date = new Date()): boolean {
  if (!e.date) return false;
  return e.date > localDateString(now);
}

export type EntriesEmptyReason = "none" | "future_only" | "out_of_period";

/**
 * Melyik üres-állapot szöveget mutassa egy időszak-szűrt grafikon/kártya,
 * ha a szűrt bejegyzésszám a megjelenítési küszöb (jellemzően 2) alatt van.
 *
 * 2026-08-17 QA-lelet: egyetlen "nincs adat" szöveg fedte le mindhárom
 * esetet — egy 52 bejegyzéses, visszatérő felhasználó ugyanazt a
 * "kezdj el naplózni" üzenetet látta, mint egy vadonatúj fiók, ha épp
 * a kiválasztott időszakba (pl. "2 hét") nem esett bejegyzés. A "csak
 * jövőbeli dátumú bejegyzés" eset külön ág, mert arra a "válts Összes
 * nézetre" tanács hatástalan (a jövőbeli dátum nem időszak kérdése —
 * "Összes" nézetben is 0 marad a számítható bejegyzés).
 *
 * A `filteredCount` paraméter azért kell (nem elég a raw/computable pár),
 * mert egy olyan felhasználónál, akinek összesen 1 számítható bejegyzése
 * van és épp "Összes" nézetben áll, a periódus semmit nem szűrt ki —
 * ott a "válts Összes nézetre" tanács önmagára mutatna, félrevezető
 * lenne. Csak akkor "out_of_period" az ok, ha a szűrés ténylegesen
 * kizárt olyan bejegyzést, ami egy tágabb nézetben látszana.
 */
export function selectEntriesEmptyReason(
  rawCount: number,
  computableCount: number,
  filteredCount: number
): EntriesEmptyReason {
  if (rawCount === 0) return "none";
  if (computableCount === 0) return "future_only";
  if (filteredCount < computableCount) return "out_of_period";
  return "none";
}

/** Egységes szöveg a fenti okhoz — Fooldal és Statisztika közös forrása. */
export const ENTRIES_EMPTY_MESSAGES: Record<EntriesEmptyReason, string> = {
  none: "Adj hozzá legalább 2 bejegyzést a grafikonok megjelenítéséhez.",
  future_only: "A bejegyzéseid jövőbeli dátummal szerepelnek, ezért még nem számíthatók a statisztikába.",
  out_of_period: "Ebben az időszakban nincs bejegyzésed. Válts az „Összes” nézetre a teljes előzményedhez.",
};
